import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { staticFeatureFlags } from '@/config/features';
import type { ControlType } from '@/constants/institution';
import type { InstitutionFuturesDecisionMaker } from '@/features/institution/futures/decision-maker/schema';
import type { InstitutionFuturesExecution } from '@/features/institution/futures/execution/schema';
import { useInstitutionDetailsAnswers, useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import {
  type DateOfBirthFieldKey,
  getInstitutionExistingIndividuals,
  getInstitutionIndividualKey,
  type InstitutionIndividualOrDefaults,
  institutionDetailsIndividualDateOfBirthFields,
} from '@/features/institution/schemas/individual';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useNationalClientIdentifierFormats } from '@/hooks/queries/use-national-client-identifier-formats';
import { getNationalClientIdentifierExample } from '@/services/api/profile/national-client-identifier-format';
import type { DeepPartial } from '@/types/generics';
import { getMonthNames } from '@/utils/date';
import {
  appendNationalClientIdentifierExample,
  getNationalClientIdentifierTypeForCountry,
  getNationalClientIdentifierTypeLabel,
  NationalClientIdentifierType,
} from '@/utils/national-client-identifier';

export type InstitutionFuturesIndividualsType =
  | Extract<InstitutionFuturesDecisionMaker, { decisionMaker: typeof ControlType.INDIVIDUAL }>
  | Extract<InstitutionFuturesExecution, { execution: typeof ControlType.INDIVIDUAL }>;

type InstitutionFuturesIndividualsOrDefaults = InstitutionFuturesIndividualsType | DeepPartial<InstitutionFuturesIndividualsType>;

type InstitutionFuturesIndividualsSection = 'decisionMaker' | 'execution';

const InstitutionFuturesIndividualNationalClientIdentifier = ({ index }: { index: number }) => {
  const form = useFormContext<InstitutionFuturesIndividualsOrDefaults>();
  const { data: countries } = useMainstreamCountries();
  const { data: nationalClientIdentifierFormats } = useNationalClientIdentifierFormats();
  const { setValue, watch } = form;
  const { t } = useTranslation();
  const nationality = watch(`individuals.${index}.nationality`);
  const identifierType = watch(`individuals.${index}.nationalClientIdentifierType`);
  const identifierTypesForCountry = nationality ? getNationalClientIdentifierTypeForCountry(nationality, countries ?? {}) : [];

  useEffect(() => {
    if (identifierTypesForCountry.length === 1 && !identifierType) {
      setValue(`individuals.${index}.nationalClientIdentifierType`, identifierTypesForCountry[0]);
    }
  }, [identifierType, identifierTypesForCountry, index, setValue]);

  if (!nationality) return;

  return (
    <>
      {identifierTypesForCountry.length > 1 && (
        <FormField
          name={`individuals.${index}.nationalClientIdentifierType`}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.individuals.individual.nationalClientIdentifierType.title')}*</FormLabel>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue(`individuals.${index}.nationalClientIdentifier`, undefined);
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={`${t('institutionDetails.individuals.individual.placeholders.nationalClientIdentifierPrefix')} ${t('institutionDetails.individuals.individual.nationalClientIdentifierType.title')}`}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {identifierTypesForCountry.map((type) => (
                    <SelectItem key={type} value={type}>
                      {getNationalClientIdentifierTypeLabel(type, nationality)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {identifierType && identifierType !== NationalClientIdentifierType.CONCAT ? (
        <FormField
          control={form.control}
          name={`individuals.${index}.nationalClientIdentifier`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getNationalClientIdentifierTypeLabel(identifierType, nationality)}*</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={appendNationalClientIdentifierExample(
                    t(`institutionDetails.individuals.individual.placeholders.nationalClientIdentifier.${identifierType}`, {
                      type: getNationalClientIdentifierTypeLabel(identifierType, nationality),
                    }),
                    getNationalClientIdentifierExample(nationalClientIdentifierFormats, { country: nationality, identifierType }),
                  )}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
};
InstitutionFuturesIndividualNationalClientIdentifier.displayName = 'InstitutionFuturesIndividualNationalClientIdentifier';

const InstitutionFuturesIndividuals = ({ section }: { section: InstitutionFuturesIndividualsSection }) => {
  const form = useFormContext<InstitutionFuturesIndividualsOrDefaults>();
  const { countriesAsOptions } = useMainstreamCountries();
  const { data: answers } = useInstitutionDetailsAnswers();
  const { data: v2Answers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const individuals = form.watch('individuals') || [];
  const individualKeys = new Set(individuals.map(getInstitutionIndividualKey));
  const existingIndividuals = getInstitutionExistingIndividuals(section === 'execution' ? answers?.decisionMaker : undefined, v2Answers).filter(
    (individual) => !individualKeys.has(getInstitutionIndividualKey(individual)),
  );

  const addExistingIndividual = (key: string) => {
    const individual = existingIndividuals.find((candidate) => getInstitutionIndividualKey(candidate) === key);

    if (!individual) return;

    form.setValue('individuals', [
      ...individuals,
      { ...structuredClone(individual), individualId: individual.individualId ?? crypto.randomUUID() },
    ] as InstitutionFuturesIndividualsOrDefaults['individuals']);
  };

  const addIndividual = () => {
    const newIndividual: InstitutionIndividualOrDefaults = {
      individualId: crypto.randomUUID(),
      firstName: '',
      lastName: '',
      dateOfBirth: {
        day: '',
        month: '',
        year: '',
      },
      countryOfBirth: '',
      localityOfBirth: '',
      nationality: '',
      nationalClientIdentifierType: undefined,
      nationalClientIdentifier: '',
    };

    form.setValue('individuals', [...individuals, newIndividual] as InstitutionFuturesIndividualsOrDefaults['individuals']);
  };

  const removeIndividual = (index: number) =>
    form.setValue(
      'individuals',
      individuals.filter((_, i) => i !== index),
    );

  return (
    <FormField
      control={form.control}
      name="individuals"
      render={({ fieldState: { error } }) => (
        <FormItem>
          <FormControl>
            <fieldset className="space-y-6">
              <legend className="flex items-center justify-between">
                <h3 className="text-base font-medium">
                  {!staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED
                    ? t('institutionDetails.individuals.individuals.title')
                    : t('institutionDetails.individuals.individual.title')}
                </h3>
              </legend>
              {individuals.map((_, index) => (
                <FormField
                  // biome-ignore lint/suspicious/noArrayIndexKey: index matches position in array
                  key={index}
                  control={form.control}
                  name={`individuals.${index}`}
                  render={() => (
                    <Card className="shadow-none" bordered>
                      <CardContent className="pt-6">
                        <FormItem className="relative space-y-0">
                          <FormControl>
                            <fieldset className="space-y-6">
                              <legend className="font-medium">
                                {t('institutionDetails.individuals.individual.title')} {!staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED ? index + 1 : ''}
                              </legend>
                              {individuals.length > 1 ? (
                                <Button
                                  className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive"
                                  size="icon"
                                  type="button"
                                  variant="ghost"
                                  onClick={() => removeIndividual(index)}
                                >
                                  <Trash2 className="size-4" />
                                  <span className="sr-only">{t('institutionDetails.individuals.individual.remove')}</span>
                                </Button>
                              ) : null}
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.firstName`}
                                render={({ field }) => (
                                  <FormItem className="md:col-span-2">
                                    <FormLabel>{t('institutionDetails.individuals.individual.firstName')}*</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder={t('institutionDetails.individuals.individual.placeholders.firstName')} value={field.value ?? ''} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.lastName`}
                                render={({ field }) => (
                                  <FormItem className="md:col-span-2">
                                    <FormLabel>{t('institutionDetails.individuals.individual.lastName')}*</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder={t('institutionDetails.individuals.individual.placeholders.lastName')} value={field.value ?? ''} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.dateOfBirth`}
                                render={({ fieldState: { error } }) => {
                                  const rootError = error?.message;
                                  const hasFieldErrors =
                                    error && Object.keys(error).some((key) => institutionDetailsIndividualDateOfBirthFields.includes(key as DateOfBirthFieldKey));

                                  return (
                                    <FormItem>
                                      <FormControl>
                                        <fieldset>
                                          <legend>{t('institutionDetails.individuals.individual.dateOfBirth')}*</legend>
                                          <div className="grid grid-cols-3 gap-4">
                                            <FormField
                                              control={form.control}
                                              name={`individuals.${index}.dateOfBirth.day`}
                                              render={({ field }) => (
                                                <FormItem className="col-span-1">
                                                  <FormLabel className="sr-only">{t('fundamentals.date.day')}</FormLabel>
                                                  <FormControl>
                                                    <Input {...field} className="no-spinner" placeholder={t('fundamentals.date.day')} type="number" />
                                                  </FormControl>
                                                  <FormMessage />
                                                </FormItem>
                                              )}
                                            />
                                            <FormField
                                              control={form.control}
                                              name={`individuals.${index}.dateOfBirth.month`}
                                              render={({ field }) => (
                                                <FormItem className="col-span-1">
                                                  <FormLabel className="sr-only">{t('fundamentals.date.month')}</FormLabel>
                                                  <Select defaultValue={field.value} onValueChange={field.onChange}>
                                                    <FormControl>
                                                      <SelectTrigger value={field.value}>
                                                        <SelectValue placeholder={t('fundamentals.date.month')} />
                                                      </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                      {getMonthNames().map((month, index) => (
                                                        <SelectItem key={month} value={(index + 1).toString()}>
                                                          {month}
                                                        </SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                  <FormMessage />
                                                </FormItem>
                                              )}
                                            />
                                            <FormField
                                              control={form.control}
                                              name={`individuals.${index}.dateOfBirth.year`}
                                              render={({ field }) => (
                                                <FormItem className="col-span-1">
                                                  <FormLabel className="sr-only">{t('fundamentals.date.year')}</FormLabel>
                                                  <FormControl>
                                                    <Input {...field} className="no-spinner" placeholder={t('fundamentals.date.year')} type="number" />
                                                  </FormControl>
                                                  <FormMessage />
                                                </FormItem>
                                              )}
                                            />
                                          </div>
                                        </fieldset>
                                      </FormControl>
                                      {rootError && !hasFieldErrors ? <FormMessage>{rootError}</FormMessage> : null}
                                    </FormItem>
                                  );
                                }}
                              />
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.nationality`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('institutionDetails.individuals.individual.nationality')}*</FormLabel>
                                    <PopoverControlled>
                                      {(_, setOpen) => (
                                        <>
                                          <PopoverTrigger asChild>
                                            <FormControl>
                                              <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                                                <PseudoInputLabel>
                                                  {field.value
                                                    ? countriesAsOptions.find((option) => option.value === field.value)?.label
                                                    : t('institutionDetails.individuals.individual.placeholders.nationality')}
                                                </PseudoInputLabel>
                                                <PseudoInputIcon>
                                                  <ChevronsUpDown />
                                                </PseudoInputIcon>
                                              </PseudoInputButton>
                                            </FormControl>
                                          </PopoverTrigger>
                                          <PopoverContent className="p-0" stretch>
                                            <PseudoInputOptions
                                              empty={t('statuses.noResults')}
                                              options={countriesAsOptions}
                                              placeholder={t('fundamentals.searchCountries')}
                                              value={field.value}
                                              onValueChange={(value) => {
                                                field.onChange(value);
                                                setOpen(false);
                                                form.setValue(`individuals.${index}.nationalClientIdentifierType`, undefined);
                                                form.setValue(`individuals.${index}.nationalClientIdentifier`, undefined);
                                              }}
                                            />
                                          </PopoverContent>
                                        </>
                                      )}
                                    </PopoverControlled>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <InstitutionFuturesIndividualNationalClientIdentifier index={index} />
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.countryOfBirth`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('institutionDetails.individuals.individual.countryOfBirth')}*</FormLabel>
                                    <PopoverControlled>
                                      {(_, setOpen) => (
                                        <>
                                          <PopoverTrigger asChild>
                                            <FormControl>
                                              <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                                                <PseudoInputLabel>
                                                  {field.value
                                                    ? countriesAsOptions.find((option) => option.value === field.value)?.label
                                                    : t('institutionDetails.individuals.individual.placeholders.countryOfBirth')}
                                                </PseudoInputLabel>
                                                <PseudoInputIcon>
                                                  <ChevronsUpDown />
                                                </PseudoInputIcon>
                                              </PseudoInputButton>
                                            </FormControl>
                                          </PopoverTrigger>
                                          <PopoverContent className="p-0" stretch>
                                            <PseudoInputOptions
                                              empty={t('statuses.noResults')}
                                              options={countriesAsOptions}
                                              placeholder={t('fundamentals.searchCountries')}
                                              value={field.value}
                                              onValueChange={(value) => {
                                                field.onChange(value);
                                                setOpen(false);
                                              }}
                                            />
                                          </PopoverContent>
                                        </>
                                      )}
                                    </PopoverControlled>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`individuals.${index}.localityOfBirth`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('institutionDetails.individuals.individual.localityOfBirth')}*</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder={t('institutionDetails.individuals.individual.placeholders.localityOfBirth')} value={field.value ?? ''} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </fieldset>
                          </FormControl>
                        </FormItem>
                      </CardContent>
                    </Card>
                  )}
                />
              ))}
              {!staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED || !individuals.length ? (
                <div className="flex flex-wrap gap-2">
                  {existingIndividuals.length ? (
                    <Select value="" onValueChange={addExistingIndividual}>
                      <SelectTrigger className="h-9 w-auto gap-2" aria-label={t('institutionDetails.individuals.individual.addFromEarlierSections')}>
                        <SelectValue placeholder={t('institutionDetails.individuals.individual.addFromEarlierSections')} />
                      </SelectTrigger>
                      <SelectContent>
                        {existingIndividuals.map((individual) => (
                          <SelectItem key={getInstitutionIndividualKey(individual)} value={getInstitutionIndividualKey(individual)}>
                            {individual.firstName} {individual.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                  <Button className="gap-2" size="sm" type="button" variant="outline" onClick={addIndividual}>
                    <Plus className="size-4" />
                    {t('institutionDetails.individuals.individual.add')}
                  </Button>
                </div>
              ) : null}
            </fieldset>
          </FormControl>
          {error && 'message' in error ? <FormMessage>{error.message}</FormMessage> : null}
        </FormItem>
      )}
    />
  );
};
InstitutionFuturesIndividuals.displayName = 'InstitutionFuturesIndividuals';

export default InstitutionFuturesIndividuals;
